import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Heart,
  MapPin,
  Shield,
  Droplets,
  Clock,
  DollarSign,
  Sparkles,
  Trash2,
  Navigation,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FavoritesPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/favorites`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFavorites(response.data);
    } catch (error) {
      console.error("Error fetching favorites:", error);
      toast.error("Failed to load favorites");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFavorite = async (restroomId) => {
    try {
      await axios.delete(`${API}/favorites/${restroomId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFavorites(favorites.filter((f) => f.restroom_id !== restroomId));
      toast.success("Removed from favorites");
    } catch (error) {
      toast.error("Failed to remove favorite");
    }
  };

  const openDirections = (lat, lng) => {
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, "_blank");
  };

  const getRatingColor = (rating) => {
    if (rating >= 4) return "text-green-500";
    if (rating >= 3) return "text-amber-500";
    return "text-red-500";
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-8" data-testid="favorites-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center">
          <button
            onClick={() => navigate("/")}
            className="flex items-center text-slate-500 hover:text-slate-700"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back
          </button>
          <h1 className="text-lg font-bold ml-4 flex items-center gap-2">
            <Heart size={20} className="text-red-500" />
            Saved Restrooms
          </h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-6">
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="skeleton h-32 rounded-xl" />
            ))}
          </div>
        ) : favorites.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Heart size={32} className="text-slate-400" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">No saved restrooms</h2>
            <p className="text-slate-500 mb-6">
              Save your favorite restrooms for quick access later.
            </p>
            <Button onClick={() => navigate("/")} className="rounded-full">
              <MapPin size={18} className="mr-2" />
              Explore Map
            </Button>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {favorites.map((restroom, index) => (
              <motion.div
                key={restroom.restroom_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="overflow-hidden hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-900">{restroom.name}</h3>
                        <p className="text-sm text-slate-500 mt-1 flex items-center gap-1">
                          <MapPin size={14} />
                          {restroom.address}
                        </p>
                        
                        <div className="flex items-center gap-4 mt-3">
                          <div className="flex items-center gap-1">
                            <Shield size={16} className={getRatingColor(restroom.safety_rating)} />
                            <span className="text-sm font-medium">{restroom.safety_rating.toFixed(1)}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Droplets size={16} className={getRatingColor(restroom.cleanliness_rating)} />
                            <span className="text-sm font-medium">{restroom.cleanliness_rating.toFixed(1)}</span>
                          </div>
                        </div>

                        <div className="flex gap-2 mt-3 flex-wrap">
                          {restroom.type === "paid" && (
                            <Badge variant="secondary" className="text-xs">
                              <DollarSign size={12} className="mr-1" />
                              Paid
                            </Badge>
                          )}
                          {restroom.is_24_hours && (
                            <Badge variant="secondary" className="text-xs">
                              <Clock size={12} className="mr-1" />
                              24h
                            </Badge>
                          )}
                          {restroom.verified && (
                            <Badge className="text-xs bg-green-100 text-green-700">
                              <Sparkles size={12} className="mr-1" />
                              Verified
                            </Badge>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveFavorite(restroom.restroom_id)}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        >
                          <Trash2 size={18} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDirections(restroom.latitude, restroom.longitude)}
                          className="text-sky-600 hover:text-sky-700 hover:bg-sky-50"
                        >
                          <Navigation size={18} />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
